import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types/index';
import { AuthService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isApproved: boolean;
  signup: (email: string, password: string, name: string, role?: 'admin' | 'sales' | 'viewer') => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('AuthProvider: Setting up auth listener...');

    // Subscribe to auth state changes
    const unsubscribe = AuthService.onAuthStateChange((userData) => {
      console.log('AuthProvider: Auth state changed, user:', userData?.email || 'null');
      setUser(userData);
      setLoading(false);
      setError(null); // Clear any previous errors when auth state updates
    });

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.log('AuthProvider: Loading timeout reached');
      setLoading(false);
    }, 5000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  // Poll for user status changes every 5 seconds (checking isActive and isApproved)
  useEffect(() => {
    if (!user?.uid) return;

    const pollUserStatus = async () => {
      try {
        const currentUserData = await AuthService.getUserData(user.uid);
        if (currentUserData) {
          // Check if user's active status has changed
          if (currentUserData.isActive === false && user.isActive !== false) {
            console.log('AuthContext: User has been deactivated! Logging out');
            await AuthService.logout();
            setUser(null);
            return;
          }
          // Check if user's approval status has changed
          if (!currentUserData.isApproved && user.isApproved) {
            console.log('AuthContext: User approval was revoked!');
            setUser(currentUserData);
            return;
          }
          // Check if user status changed
          if (currentUserData.status !== user.status) {
            console.log('AuthContext: User status changed from', user.status, 'to', currentUserData.status);
            setUser(currentUserData);
          }
        } else {
          // User document was deleted
          console.log('AuthContext: User document no longer exists, logging out');
          await AuthService.logout();
          setUser(null);
        }
      } catch (err: any) {
        console.error('AuthContext: Error polling user status:', err.message);
      }
    };

    // Poll every 5 seconds
    const interval = setInterval(pollUserStatus, 5000);

    return () => clearInterval(interval);
  }, [user?.uid]);

  const signup = async (
    email: string,
    password: string,
    name: string,
    role: 'admin' | 'sales' | 'viewer' = 'viewer'
  ) => {
    try {
      setError(null);
      await AuthService.signup(email, password, name, role);
      // Don't set loading - let the onAuthStateChange listener handle it
      // The listener will be triggered once Firebase Auth is ready
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      await AuthService.login(email, password);
      // Don't set loading to true - let the auth state change listener handle it
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  const loginWithGoogle = async () => {
    try {
      setError(null);
      await AuthService.loginWithGoogle();
      // Don't set loading to true - let the auth state change listener handle it
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await AuthService.logout();
      setUser(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        isApproved: user?.isApproved ?? false,
        signup,
        login,
        loginWithGoogle,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
